import {Button, Tag} from "antd";
import {CrownOutlined, MessageOutlined} from "@ant-design/icons";
import styled from "styled-components";
import {useMediaQuery} from "@uidotdev/usehooks";
import {Link, useNavigate} from "react-router-dom";
import {IJobDetails} from "../../../services/types.ts";
import {colors} from "../../../util/colors.ts";
import {useSubscriptionEndDate} from "../../../hooks/useSubscriptionEndDate.ts";

export const JobPoolItem = ({item, isJobSearchAutomatic}: { item: IJobDetails, isJobSearchAutomatic: boolean }) => {
    const navigate = useNavigate();

    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const {endDate} = useSubscriptionEndDate();
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    const onCoverLetterClick = async (job:IJobDetails)=>{
        // Check subscription status
        if (!hasActiveSubscription) {
            // Open silverplus page in new tab
            window.open('/silverplus', '_blank');
            return;
        }

        try {
            // Fetch complete job details including description
            const { getJobById, startXanoAiCoverLetterSession } = await import('../../../services/api');
            const fullJobDetails = await getJobById(job.id.toString());

            const title = fullJobDetails.title || '';
            const description = fullJobDetails.description || '';
            const jobRole = [title, description].filter(Boolean).join('\n\n') || 'Job Position';

            // Start session with job details
            const sessionResponse = await startXanoAiCoverLetterSession(jobRole, job.id);

            // Open cover letter page in new tab
            window.open(`/cover-letter/${sessionResponse.session_id}`, '_blank');
        } catch (error) {
            console.error('Error fetching job details or starting session:', error);
            // If API calls fail, show error to user
            // Note: We no longer use localStorage fallback since the new flow handles job context through sessions
        }
    }

    const showLogo = item.has_logo === true && item._client_logo?.url;
    const isHighlighted = item.has_highlight === true;
    const isTopPosition = item.has_top_position === true;
    const isSilverJob = item?._client?.profile_name === 'SilverJob';

    const bgStyle = isHighlighted
        ? {
            borderLeft: '4px solid rgb(2,176,187)',
            background: "linear-gradient(275deg, rgb(42 123 155 / 10%) 0%, rgb(2 176 187 / 10%) 50%)",
        }
        : isSilverJob
            ? {
                borderLeft: '4px solid #d4af37',
                background: "linear-gradient(275deg, rgb(212 175 55 / 10%) 0%, rgb(192 192 192 / 12%) 50%)",
            }
            : undefined;

    return (
        <JobRowItemWrapper>
            <BgWrapper style={bgStyle}>
                <ItemDetails>
                    <InfoContainer>
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                            {showLogo && (
                                <CompanyLogo
                                    src={ item._client_logo?.url}
                                    alt={item._client?.profile_name ?? 'Firmenlogo'}
                                />
                            )}
                            <div style={{flex: 1}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                                    <Link to={`/jobs/${item.id}`} style={{color: 'black'}}>
                                        <h3 style={{width: isMobile ? "fit-content" : "auto", marginTop: 0, marginBottom: 0}}>
                                            {item.title}
                                        </h3>
                                    </Link>
                                    {isTopPosition && (
                                        <Tag color="gold" icon={<CrownOutlined/>}>Top</Tag>
                                    )}
                                </div>
                            </div>
                        </div>
                        {item?.search_location_label &&
                            <PoolItemMeta>Ort: {item.search_location_label}</PoolItemMeta>}
                        {item?.work_load_label &&
                            <PoolItemMeta>Arbeitspensum: {item.work_load_label}</PoolItemMeta>}
                        {item?.homeoffice_id > 1 &&
                            <PoolItemMeta>Homeoffice: {item.homeoffice_label}</PoolItemMeta>}
                        {item?._client?.profile_name &&
                            <PoolItemMeta>Arbeitgeber: {item._client?.profile_name}</PoolItemMeta>}
                        {item?.potential_jobs_label && isJobSearchAutomatic &&
                            <PoolItemMeta><b>Match über Ihre vorstellbare
                                Tätigkeit: {item.potential_jobs_label}</b></PoolItemMeta>}
                    </InfoContainer>
                </ItemDetails>
                <ItemBtns>
                    <Button
                        onClick={() => navigate(`/jobs/${item.id}`)}
                        shape="round"
                        style={{width: isMobile ? '100%' : 200}}
                        type="primary"
                    >
                        Ansehen
                    </Button>
                    <Button
                        onClick={()=>onCoverLetterClick(item)}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Anschreiben erstellen
                    </Button>
                    {item._outreaches_to_candidate && (
                        <CandOutreached>
                            <MessageOutlined/>
                            <p>Kontaktiert</p>
                        </CandOutreached>
                    )}
                </ItemBtns>
            </BgWrapper>
        </JobRowItemWrapper>
    )
}

export const JobRowItemWrapper = styled.div`
    background: ${colors.bg};
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 767px) {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`

export const RowItemWrapper = styled.div`
    padding: 0 100px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 900px) {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`
export const BgWrapper = styled.div`
    width: 100%;
    padding: 8px;
    min-height: 130px;
    background: white;
    border-bottom: 1px solid #d9d9d9;
    border-radius: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 767px) {
        background: ${colors.bg} !important;
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`

export const ItemDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 80%;
    justify-content: space-between;
    @media (max-width: 767px) {
        align-items: flex-start;
        flex-direction: column;
        width: calc(100% - 4px);
        padding: 8px;
        box-shadow: 0 .125rem .25rem rgba(45, 45, 45, .12), 0 .0625rem .1875rem rgba(45, 45, 45, .16), 0 0 .125rem rgba(45, 45, 45, .2);
        background: white;
        border-radius: 4px;
        margin: 0 2px;
    }
`

export const InfoContainer = styled.div`
    flex: 1;
    min-width: 0;
    @media (max-width: 767px) {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }
`

export const ItemBtns = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 16px;
    gap: 8px;
    flex-shrink: 0;
    @media (max-width: 767px) {
        padding-top: 3px;
        margin: 0;
        width: 100%;
        align-items: center;
    }
`

export const PoolItemMeta = styled.p`
    margin: 0 0 4px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    color: #696969
`;

const CandOutreached = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    p {
        margin: 0;
    }
`

const CompanyLogo = styled.img`
    width: 48px;
    height: 48px;
    padding: 2px;
    border-radius: 8px;
    object-fit: contain;
    border: 1px solid #f0f0f0;
    flex-shrink: 0;
`
